import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AccrueOpContracts,
  CreateOpContract,
  CreateOpContractRate,
  OpContract,
  OpContractAccrual,
  OpContractAccrualRun,
  OpContractAccrualRunWithApprovals,
  OpContractDisclosure,
  OpContractListRow,
  OpContractRate,
  OpContractRunResult,
  OpContractStatus,
  OpContractWithAccruals,
  RenewOpContract,
  ReverseOpContracts,
  TerminateOpContract,
  UpdateOpContract,
} from '@dcc-erp/shared';

import { api, apiJson, withSodOverride } from '../api';

// Cache-key scope — keyed by companyId per the convention used in lva.ts / ar.ts.
const opContractsKey = (companyId: string | undefined) => ['op-contracts', companyId] as const;
const accrualsKey = (companyId: string | undefined) => ['op-contract-accruals', companyId] as const;
// Monthly accrual-run headers (M3, docType OSC_RUN) — the run-history tab + the
// run approval panel read off this key.
const accrualRunsKey = (companyId: string | undefined) =>
  ['op-contract-accrual-runs', companyId] as const;
const accrualRunKey = (companyId: string | undefined, id: string) =>
  ['op-contract-accrual-runs', companyId, id] as const;
// The single-contract detail key (mirror useOpContract's queryKey) so engine
// verbs refresh the open detail modal + its approval chain.
const opContractKey = (companyId: string | undefined, id: string) =>
  ['op-contracts', companyId, id] as const;
// M5a escalation — the per-contract effective-dated rate schedule. The detail
// modal's rate-schedule section reads off this key; add/delete invalidate it
// plus the single-contract detail (currentRate moves) and the register list.
const opContractRatesKey = (companyId: string | undefined, contractId: string) =>
  ['op-contract-rates', companyId, contractId] as const;

export interface OpContractFilters {
  contractType?: string;
  vendorId?: string;
  branchId?: string;
  status?: OpContractStatus;
}

export function useOpContracts(companyId: string | undefined, filters: OpContractFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.contractType) qs.set('contractType', filters.contractType);
  if (filters.vendorId) qs.set('vendorId', filters.vendorId);
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.status) qs.set('status', filters.status);
  const search = qs.toString();
  return useQuery({
    queryKey: [...opContractsKey(companyId), filters],
    queryFn: () =>
      api<OpContractListRow[]>(`/companies/${companyId}/op-contracts${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useOpContract(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['op-contracts', companyId, id],
    queryFn: () => api<OpContractWithAccruals>(`/companies/${companyId}/op-contracts/${id}`),
    enabled: !!companyId && !!id,
  });
}

// M4 TFRS 16 — para-53 disclosure aggregation, keyed by company + year. The
// disclosure tab reads off this key; year is driven by the page-head selector.
const disclosureKey = (companyId: string | undefined, year: number) =>
  ['op-contract-disclosure', companyId, year] as const;

// Fetch the TFRS/IFRS 16 para-53 disclosure for a fiscal year (POSTED accrual
// amounts grouped by exemptionBasis + the short-term / low-value totals).
export function useOpContractDisclosure(companyId: string | undefined, year: number) {
  return useQuery({
    queryKey: disclosureKey(companyId, year),
    queryFn: () =>
      api<OpContractDisclosure>(
        `/companies/${companyId}/op-contracts/disclosure?year=${encodeURIComponent(year)}`,
      ),
    enabled: !!companyId,
  });
}

export interface OpContractAccrualFilters {
  period?: string;
  contractId?: string;
}

export function useOpContractAccruals(
  companyId: string | undefined,
  filters: OpContractAccrualFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.period) qs.set('period', filters.period);
  if (filters.contractId) qs.set('contractId', filters.contractId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...accrualsKey(companyId), filters],
    queryFn: () =>
      api<OpContractAccrual[]>(
        `/companies/${companyId}/op-contracts/accruals${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

// M5b true-up — a contract's LINKABLE accruals (POSTED + not already trued-up)
// for the AP-bill true-up picker. Keyed under the same 'op-contract-accruals'
// prefix as the company-wide accruals so the AP link/unlink mutations (which
// invalidate that prefix) refresh this picker too. Scoped by contractId so each
// contract's picker caches independently; only fetched once a contract is chosen.
export function useLinkableAccruals(companyId: string | undefined, contractId: string | undefined) {
  return useQuery({
    queryKey: [...accrualsKey(companyId), 'linkable', contractId ?? ''],
    queryFn: () =>
      api<OpContractAccrual[]>(
        `/companies/${companyId}/op-contracts/${contractId}/accruals?linkable=true`,
      ),
    enabled: !!companyId && !!contractId,
  });
}

export function useCreateOpContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateOpContract) =>
      api<OpContract>(`/companies/${companyId}/op-contracts`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: opContractsKey(companyId) });
    },
  });
}

export function useUpdateOpContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOpContract }) =>
      api<OpContract>(`/companies/${companyId}/op-contracts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: opContractsKey(companyId) });
    },
  });
}

export function useTerminateOpContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TerminateOpContract }) =>
      api<OpContract>(`/companies/${companyId}/op-contracts/${id}/terminate`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: opContractsKey(companyId) });
    },
  });
}

export function useRenewOpContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RenewOpContract }) =>
      api<OpContract>(`/companies/${companyId}/op-contracts/${id}/renew`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: opContractsKey(companyId) });
    },
  });
}

export function useAccrueOpContracts(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: AccrueOpContracts; idempotencyKey?: string }) =>
      // POST /accrue now PREPARES + submits a run (M3) — it returns the run header
      // shape ({ id, period, contractCount, totalAmount }), NOT the old direct-post
      // OpContractRunResult ({ journalEntryIds, ... }). Type it as the run header.
      api<OpContractAccrualRun>(`/companies/${companyId}/op-contracts/accrue`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: opContractsKey(companyId) });
      void qc.invalidateQueries({ queryKey: accrualsKey(companyId) });
      // Accrue posts JVs — invalidate journal-entries cache too.
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useReverseOpContracts(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: ReverseOpContracts; idempotencyKey?: string }) =>
      api<OpContractRunResult>(`/companies/${companyId}/op-contracts/reverse`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: opContractsKey(companyId) });
      void qc.invalidateQueries({ queryKey: accrualsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// ============================================================
// Central-engine approval hooks (M3 maker-checker, docType OSC) — mirror the
// AP-bill engine hooks (ap.ts). The OSC chain gates contract REGISTER /
// TERMINATE; final-step approval applies the lifecycle flip server-side, so we
// invalidate the list + the single-contract detail (the open modal/timeline)
// and the accruals (eligibility shifts once a registration is approved).
// ============================================================

function invalidateOpContractEngine(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
  id: string,
) {
  void qc.invalidateQueries({ queryKey: opContractsKey(companyId) });
  void qc.invalidateQueries({ queryKey: opContractKey(companyId, id) });
  void qc.invalidateQueries({ queryKey: accrualsKey(companyId) });
}

// Submit a DRAFT / RETURNED contract into the engine chain (re-submit path).
export function useSubmitOpContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, OpContract>('POST', `/companies/${companyId}/op-contracts/${id}/submit`),
    onSuccess: (_data, id) => invalidateOpContractEngine(qc, companyId, id),
  });
}

// Approve the current engine step (final-step approval applies the lifecycle
// flip: REGISTER stays ACTIVE, TERMINATE → TERMINATED).
export function useApproveOpContractStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      comment,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; comment?: string; sodOverrideReason?: string }, OpContract>(
        'POST',
        `/companies/${companyId}/op-contracts/${id}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: (_data, { id }) => invalidateOpContractEngine(qc, companyId, id),
  });
}

// Reject the contract approval at a step (terminal — REGISTER reject voids the
// registration; TERMINATE reject keeps the contract ACTIVE).
export function useRejectOpContractStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, OpContract>(
        'POST',
        `/companies/${companyId}/op-contracts/${id}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: (_data, { id }) => invalidateOpContractEngine(qc, companyId, id),
  });
}

// Return a PENDING contract for revision (RETURNED — resubmittable).
export function useReturnOpContract(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, OpContract>(
        'POST',
        `/companies/${companyId}/op-contracts/${id}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: (_data, { id }) => invalidateOpContractEngine(qc, companyId, id),
  });
}

// ============================================================
// Monthly accrual-run approval hooks (M3 maker-checker, docType OSC_RUN). The
// run gates the monthly accrual: POST /accrue (useAccrueOpContracts) now creates
// a PENDING run + PENDING accrual rows (NO JV); the bucket JVs post only when
// the run reaches FINAL approval. So the engine verbs below invalidate the run
// keys + the op-contracts list + the accruals (rows flip PENDING→POSTED on final
// approval, or are deleted on rejection) + journal-entries (final approval posts
// the bucket JVs).
// ============================================================

function invalidateAccrualRunEngine(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
  id: string,
) {
  void qc.invalidateQueries({ queryKey: accrualRunsKey(companyId) });
  void qc.invalidateQueries({ queryKey: accrualRunKey(companyId, id) });
  void qc.invalidateQueries({ queryKey: opContractsKey(companyId) });
  void qc.invalidateQueries({ queryKey: accrualsKey(companyId) });
  // Final-step approval posts the bucket JVs — refresh the journal cache too.
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
}

// List the monthly accrual runs (newest first), optionally filtered by period.
export function useAccrualRuns(companyId: string | undefined, period?: string) {
  const qs = period ? `?period=${encodeURIComponent(period)}` : '';
  return useQuery({
    queryKey: [...accrualRunsKey(companyId), { period: period ?? null }],
    queryFn: () =>
      api<OpContractAccrualRun[]>(`/companies/${companyId}/op-contracts/accrual-runs${qs}`),
    enabled: !!companyId,
  });
}

// Fetch one run with its materialized approval chain (drives the run panel).
export function useAccrualRun(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: accrualRunKey(companyId, id ?? ''),
    queryFn: () =>
      api<OpContractAccrualRunWithApprovals>(
        `/companies/${companyId}/op-contracts/accrual-runs/${id}`,
      ),
    enabled: !!companyId && !!id,
  });
}

// Approve the current run step (final-step approval posts every bucket JV and
// flips the run accrual rows to POSTED).
export function useApproveAccrualRunStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      comment,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; comment?: string; sodOverrideReason?: string }, OpContractAccrualRun>(
        'POST',
        `/companies/${companyId}/op-contracts/accrual-runs/${id}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: (_data, { id }) => invalidateAccrualRunEngine(qc, companyId, id),
  });
}

// Reject the run at a step (terminal — no JV was posted; the PENDING accrual
// rows are deleted so the period can be re-run).
export function useRejectAccrualRunStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, OpContractAccrualRun>(
        'POST',
        `/companies/${companyId}/op-contracts/accrual-runs/${id}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: (_data, { id }) => invalidateAccrualRunEngine(qc, companyId, id),
  });
}

// Return a PENDING run for revision (RETURNED — resubmittable).
export function useReturnAccrualRun(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, OpContractAccrualRun>(
        'POST',
        `/companies/${companyId}/op-contracts/accrual-runs/${id}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: (_data, { id }) => invalidateAccrualRunEngine(qc, companyId, id),
  });
}

// ============================================================
// M5a escalation — effective-dated rate-schedule CRUD. The contract's base
// monthlyAmount is the default rate; each row overrides it from its
// effectiveFrom (YYYYMM) period onward. The accrual reads the period-effective
// rate server-side; here we just surface + mutate the schedule. Mutations
// invalidate the rate-schedule key + the single-contract detail (rates +
// currentRate live there) + the register list (its currentRate column moves).
// ============================================================

function invalidateOpContractRates(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
  contractId: string,
) {
  void qc.invalidateQueries({ queryKey: opContractRatesKey(companyId, contractId) });
  void qc.invalidateQueries({ queryKey: opContractKey(companyId, contractId) });
  void qc.invalidateQueries({ queryKey: opContractsKey(companyId) });
}

// List a contract's rate schedule (effectiveFrom asc).
export function useOpContractRates(companyId: string | undefined, contractId: string | undefined) {
  return useQuery({
    queryKey: opContractRatesKey(companyId, contractId ?? ''),
    queryFn: () =>
      api<OpContractRate[]>(`/companies/${companyId}/op-contracts/${contractId}/rates`),
    enabled: !!companyId && !!contractId,
  });
}

// Add an effective-dated rate row ({ effectiveFrom, monthlyAmount, note? }).
export function useAddOpContractRate(
  companyId: string | undefined,
  contractId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateOpContractRate) =>
      apiJson<CreateOpContractRate, OpContractRate>(
        'POST',
        `/companies/${companyId}/op-contracts/${contractId}/rates`,
        data,
      ),
    onSuccess: () => invalidateOpContractRates(qc, companyId, contractId ?? ''),
  });
}

// Delete a rate-schedule row by id.
export function useDeleteOpContractRate(
  companyId: string | undefined,
  contractId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rateId: string) =>
      apiJson<undefined, void>(
        'DELETE',
        `/companies/${companyId}/op-contracts/${contractId}/rates/${rateId}`,
      ),
    onSuccess: () => invalidateOpContractRates(qc, companyId, contractId ?? ''),
  });
}
